import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function ClaimAccount() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/claim/${token}`);
        setEmail(data.email);
        setName(data.name);
        setValid(true);
      } catch {
        setValid(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/auth/claim/${token}`, { password });
      // Persist a one-time welcome banner flag for the dashboard
      try {
        sessionStorage.setItem("jdsn_welcome_message", data?.welcome_message || "Welcome to JDSN Lawn Care Customer Portal");
        sessionStorage.setItem("jdsn_activate_promo", "ACTIVATE10");
      } catch { /* noop */ }
      toast.success("Welcome to JDSN Lawn Care!");
      // Reload the auth context by full navigation so the session cookie is picked up
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim account");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#047857]" /></div>;
  }
  if (!valid) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-extrabold tracking-tighter font-[Outfit] text-gray-900">Link expired or invalid</h1>
        <p className="mt-2 text-gray-600">Ask JDSN Lawn Care to send you a new claim link.</p>
        <Button onClick={() => navigate("/")} className="mt-6 rounded-full">Go home</Button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-10" data-testid="claim-page">
      <div className="bg-white rounded-3xl border border-gray-200 p-7 shadow-sm">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#047857]" />
          <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Claim your account</div>
        </div>
        <h1 className="mt-1 text-3xl font-extrabold font-[Outfit] tracking-tighter text-gray-900">Welcome, {name || email.split("@")[0]}</h1>
        <p className="mt-1 text-sm text-gray-600">Set a password for <strong>{email}</strong> to start managing your bookings.</p>

        <div className="mt-6">
          <label className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500">Choose a password</label>
          <div className="mt-1 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-gray-400" />
            <Input
              type="password"
              autoComplete="new-password"
              minLength={8}
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="claim-password"
            />
          </div>
        </div>
        <Button
          className="mt-6 w-full rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold py-6 shadow"
          onClick={submit}
          disabled={submitting}
          data-testid="claim-submit"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Claim my account"}
        </Button>
      </div>
    </div>
  );
}
